import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { therapyService, TherapySession } from '../services/therapyService';
import PageHeader from '../components/PageHeader';
import AIConsentModal, { hasAIConsent } from '../components/AIConsentModal';
import { 
  MessageCircle, 
  Send, 
  Trash2, 
  Plus,
  Loader2,
  Brain,
  Heart,
  Sparkles,
  Menu,
  X,
  AlertCircle,
  CreditCard,
  Share2,
  Download,
  Copy,
  Check
} from 'lucide-react';

// Simple markdown renderer for AI responses
const renderMarkdown = (text: string) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  
  const flushList = () => {
    if (listItems.length > 0 && listType) {
      const items = listItems.map((item, i) => (
        <li key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
      ));
      if (listType === 'ul') {
        elements.push(<ul key={elements.length} className="list-disc space-y-1 my-2">{items}</ul>);
      } else {
        elements.push(<ol key={elements.length} className="list-decimal space-y-1 my-2">{items}</ol>);
      }
      listItems = [];
      listType = null;
    }
  };
  
  const formatInline = (line: string): string => {
    return line
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-gray-200 px-1 rounded text-sm">$1</code>');
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <h4 key={elements.length} className="font-bold text-base mt-3 mb-1"
            dangerouslySetInnerHTML={{ __html: formatInline(line.slice(4)) }} />
      );
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <h3 key={elements.length} className="font-bold text-lg mt-3 mb-1"
            dangerouslySetInnerHTML={{ __html: formatInline(line.slice(3)) }} />
      );
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(
        <h2 key={elements.length} className="font-bold text-xl mt-3 mb-1"
            dangerouslySetInnerHTML={{ __html: formatInline(line.slice(2)) }} />
      );
    }
    else if (line.match(/^[\-\*]\s/)) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(line.replace(/^[\-\*]\s/, ''));
    }
    else if (line.match(/^\d+\.\s/)) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(line.replace(/^\d+\.\s/, ''));
    }
    else if (line.trim() === '') {
      flushList();
      elements.push(<div key={elements.length} className="h-2" />);
    }
    else {
      flushList();
      elements.push(
        <p key={elements.length} className="my-1"
           dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
      );
    }
  }
  
  flushList();
  return <div className="space-y-0">{elements}</div>;
};

export default function TherapyChatPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<TherapySession[]>([]);
  const [activeSession, setActiveSession] = useState<TherapySession | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [isWaitingForFirstToken, setIsWaitingForFirstToken] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);

  // Dispatch event to hide BottomNavigation when sidebar is open on mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      window.dispatchEvent(new CustomEvent('mobile-drawer-toggle', { detail: { open: isSidebarOpen } }));
    }
    return () => {
      window.dispatchEvent(new CustomEvent('mobile-drawer-toggle', { detail: { open: false } }));
    };
  }, [isSidebarOpen]);

  const [creditError, setCreditError] = useState<string | null>(null);
  const [showAIConsent, setShowAIConsent] = useState(!hasAIConsent());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingContentRef = useRef('');

  useEffect(() => {
    loadSessions();
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent, scrollToBottom]);

  const loadSessions = async () => {
    try {
      const sessionsData = await therapyService.getSessions();
      setSessions(sessionsData);
      
      if (sessionsData.length > 0) {
        loadSession(sessionsData[0].id);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sessionId: string) => {
    try {
      const session = await therapyService.getSession(sessionId);
      setActiveSession(session);
      setMessages(session.messages || []);
      setCreditError(null);
      setIsSidebarOpen(false);
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const createNewSession = async () => {
    try {
      const newSession = await therapyService.createSession({
        title: 'Sesiune Nouă',
        sessionType: 'GENERAL_SUPPORT'
      });
      setSessions([newSession, ...sessions]);
      setActiveSession(newSession);
      setMessages([]);
      setCreditError(null);
      setIsSidebarOpen(false);
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const deleteSession = async (sessionId: string) => {
    if (!confirm('Sigur vrei să ștergi această sesiune?')) return;
    
    try {
      await therapyService.deleteSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      
      if (activeSession?.id === sessionId) {
        setActiveSession(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !activeSession || sending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setSending(true);
    setCreditError(null);
    streamingContentRef.current = '';
    setStreamingContent('');
    setIsWaitingForFirstToken(true);

    const newUserMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessage,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://fiifit.ro'}/api/therapy/sessions/${activeSession.id}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: userMessage })
      });

      // Handle credit errors (402)
      if (response.status === 402) {
        const errorData = await response.json();
        setCreditError(errorData.message || 'Credite insuficiente');
        setSending(false);
        setIsWaitingForFirstToken(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Eroare server' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Read SSE stream  
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Stream nu este disponibil');
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;

          try {
            const data = JSON.parse(dataStr);

            if (data.error) {
              console.error('Streaming error:', data.error);
              setSending(false);
              setStreamingContent('');
              setIsWaitingForFirstToken(false);
              return;
            }

            if (data.chunk) {
              setIsWaitingForFirstToken(false);
              streamingContentRef.current += data.chunk;
              setStreamingContent(streamingContentRef.current);
            }

            if (data.done) {
              const finalContent = streamingContentRef.current;
              setMessages(prev => [...prev, {
                id: data.messageId || Date.now().toString(),
                role: 'assistant',
                content: finalContent,
                createdAt: new Date().toISOString()
              }]);
              setStreamingContent('');
              streamingContentRef.current = '';
              setSending(false);
              setIsWaitingForFirstToken(false);
              return;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      }

      // If stream ends without done signal
      if (streamingContentRef.current) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: 'assistant',
          content: streamingContentRef.current,
          createdAt: new Date().toISOString()
        }]);
      }
      setStreamingContent('');
      streamingContentRef.current = '';
      setSending(false);
      setIsWaitingForFirstToken(false);

    } catch (error: any) {
      console.error('Streaming error:', error);
      setSending(false);
      setStreamingContent('');
      streamingContentRef.current = '';
      setIsWaitingForFirstToken(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Export / Distribuie sesiune
  const [copied, setCopied] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const buildSessionText = () => {
    const title = activeSession ? sessions.find(s => s.id === activeSession.id)?.title || 'Sesiune Terapeutică' : 'Sesiune Terapeutică';
    const date = new Date().toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' });
    let text = `${title}\nData: ${date}\nFII FIT - Chat Terapeutic AI\n${'─'.repeat(50)}\n\n`;
    messages.forEach((msg: any) => {
      const time = new Date(msg.createdAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
      const role = msg.role === 'user' ? '👤 Tu' : '🧠 Psiholog AI';
      text += `[${time}] ${role}:\n${msg.content}\n\n`;
    });
    return text;
  };

  const handleCopySession = async () => {
    const text = buildSessionText();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    setShowExportMenu(false);
  };

  const handleDownloadSession = () => {
    const text = buildSessionText();
    const title = activeSession ? sessions.find(s => s.id === activeSession.id)?.title || 'sesiune' : 'sesiune';
    const filename = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.txt`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const handleShareSession = async () => {
    const text = buildSessionText();
    const title = activeSession ? sessions.find(s => s.id === activeSession.id)?.title || 'Sesiune Terapeutică' : 'Sesiune Terapeutică';
    if (navigator.share) {
      await navigator.share({ title, text });
    } else {
      await handleCopySession();
    }
    setShowExportMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Chat Terapeutic AI" />
        <div className="p-4">
          <div className="text-center py-8">Se încarcă...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Chat Terapeutic AI" />

      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-4">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden flex h-[calc(100vh-80px)] sm:h-[calc(100vh-200px)] relative">
          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div
            className={`${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0 ${
              isSidebarOpen ? 'w-80' : 'md:w-0'
            } fixed md:relative inset-y-0 left-0 z-50 md:z-auto transition-all duration-300 border-r border-gray-200 flex flex-col bg-gray-50 h-full md:h-auto`}
            style={{
              paddingTop: 'env(safe-area-inset-top)',
            }}
          >
            {isSidebarOpen && (
              <>
                <div className="p-4 border-b border-gray-200">
                  <button
                    onClick={createNewSession}
                    className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Sesiune Nouă
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {sessions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Nicio sesiune încă</p>
                      <p className="text-xs mt-1">Începe o conversație nouă</p>
                    </div>
                  ) : (
                    sessions.map((session) => (
                      <div
                        key={session.id}
                        onClick={() => loadSession(session.id)}
                        className={`p-3 rounded-xl cursor-pointer transition-all border-2 group ${
                          activeSession?.id === session.id
                            ? 'bg-purple-50 border-purple-500'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-gray-900 truncate">
                              {session.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(session.updatedAt).toLocaleDateString('ro-RO')}
                            </p>
                            {session._count && (
                              <p className="text-xs text-purple-600 mt-1">
                                {session._count.messages} mesaje
                              </p>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSession(session.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center gap-3">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="text-gray-600 hover:text-gray-900"
              >
                {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h3 className="font-semibold text-gray-900 flex-1 truncate">
                {activeSession ? sessions.find(s => s.id === activeSession.id)?.title || 'Chat' : 'Sesiune Nouă'}
              </h3>

              {/* Export / Distribuie buton */}
              {messages.length > 0 && activeSession && (
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Exportă / Distribuie sesiunea"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Exportă</span>
                  </button>

                  {showExportMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                      <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
                        <button
                          onClick={handleCopySession}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
                          <span>{copied ? 'Copiat!' : 'Copiază conversația'}</span>
                        </button>
                        <button
                          onClick={handleDownloadSession}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Download className="w-4 h-4 text-gray-500" />
                          <span>Descarcă (.txt)</span>
                        </button>
                        <button
                          onClick={handleShareSession}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Share2 className="w-4 h-4 text-gray-500" />
                          <span>Distribuie</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Credit Error Banner */}
            {creditError && (
              <div className="mx-3 sm:mx-6 mt-3 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-orange-800">{creditError}</p>
                  <button
                    onClick={() => navigate('/extra-credits')}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-orange-700 hover:text-orange-900 bg-orange-100 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    Cumpără Credite
                  </button>
                </div>
                <button onClick={() => setCreditError(null)} className="text-orange-400 hover:text-orange-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Messages */}
            {!activeSession ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                  <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Brain className="w-12 h-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Chat Terapeutic AI
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Vorbește cu psihologul virtual pentru suport emoțional instant. 
                    Confidențial și personalizat.
                  </p>
                  <button
                    onClick={createNewSession}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all inline-flex items-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Începe o Conversație
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4">
                  {messages.length === 0 && !creditError && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Heart className="w-8 h-8 text-purple-600" />
                      </div>
                      <p className="text-gray-600 mb-2">Cum te pot ajuta astăzi?</p>
                      <p className="text-sm text-gray-500">
                        Scrie un mesaj pentru a începe conversația
                      </p>
                    </div>
                  )}

                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[78%] sm:max-w-[75%] rounded-2xl px-3 py-2 sm:px-5 sm:py-3 text-sm sm:text-base break-words overflow-hidden ${
                          message.role === 'user'
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center gap-2 mb-1">
                            <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                            <span className="text-xs font-semibold text-purple-600">Psiholog AI</span>
                          </div>
                        )}
                        {message.role === 'assistant' ? (
                          <div className="break-words leading-relaxed text-sm sm:text-base">
                            {renderMarkdown(message.content)}
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap leading-relaxed text-sm sm:text-base">
                            {message.content}
                          </p>
                        )}
                        <p className={`text-xs mt-1 ${
                          message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {new Date(message.createdAt).toLocaleTimeString('ro-RO', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Streaming / Typing indicator */}
                  {(isWaitingForFirstToken || streamingContent) && (
                    <div className="flex justify-start">
                      <div className="max-w-[78%] sm:max-w-[75%] rounded-2xl px-3 py-2 sm:px-5 sm:py-3 bg-gray-100 text-gray-900 text-sm sm:text-base">
                        <div className="flex items-center gap-2 mb-2">
                          <Brain className={`w-4 h-4 text-purple-600 ${isWaitingForFirstToken ? 'animate-pulse' : ''}`} />
                          <span className="text-xs font-semibold text-purple-600">Psiholog AI</span>
                        </div>
                        {isWaitingForFirstToken && !streamingContent ? (
                          <div className="flex items-center gap-2 py-1">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                          </div>
                        ) : (
                          <div className="break-words leading-relaxed">
                            {renderMarkdown(streamingContent)}
                            <span className="inline-block w-0.5 h-5 bg-purple-500 ml-0.5 animate-pulse align-text-bottom" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50">
                  <div className="flex gap-2 sm:gap-3">
                    <textarea
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Scrie mesajul tău aici..."
                      rows={2}
                      disabled={sending}
                      className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none disabled:opacity-50"
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || sending}
                      className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    ⚠️ Răspunsurile sunt generate de <strong>FII FIT AI</strong> și au caracter strict informativ. Datele tale sunt trimise către FII FIT pentru personalizare. Nu înlocuiesc terapia psihologică profesională, diagnosticul sau tratamentul. În caz de urgență, contactați un specialist sau apelați 112. Surse: ghiduri APA, OMS.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* AI Consent Modal */}
      <AIConsentModal
        isOpen={showAIConsent}
        onAccept={() => setShowAIConsent(false)}
        onDecline={() => navigate('/dashboard')}
      />
    </div>
  );
}
